/**
 * setupstate.ts -- what each scanned setup means AGAINST THE LIVE BOOK.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Andrei, 2026-08-13: *"I believe we need to show the latest and greatest setup. If
 * there's an active trade exist for the asset in the same direction, we should signal
 * the user that everything is on track. If the setup was generated in the opposite
 * direction to the existing active trade, we need to highlight this setup for
 * checking. If we already have a limit order in place at the level, we can highlight
 * that the setup is armed already. Also, I would like to see armed trades that are
 * not a part of any setups reported after scan (candidates for cancelation)"*
 *
 * The Setups tab was a flat log of what the scan said. It said nothing about the 39
 * positions and 49 orders he is actually holding while he reads it, so every row had
 * to be cross-checked in his head against another tab.
 *
 * WHERE THE JOIN LIVES, AND WHY HERE RATHER THAN IN THE API
 * --------------------------------------------------------
 * CLIENT SIDE, from the two payloads the panel already holds.
 *
 * `/api/setups` reads the scan's alerts table, which is SHARED ACROSS USERS -- the
 * morning scan is one scan, not one per account. The account snapshot is PER USER and
 * per wallet address. Teaching `/api/setups` to read a book would give a shared,
 * cacheable market-data route a per-user dimension, and its response could then never
 * be served to two people from one read.
 *
 * `advicetab.ts::didIAct` already settled this exact question for the advice record:
 * the record comes from its own route, the live half comes from the snapshot, and the
 * crossing happens in the renderer. This is the same shape, so it is the same answer
 * rather than a second pattern.
 *
 * ONE JOIN KEY, AND IT IS THE PREFIXED FORM
 * -----------------------------------------
 * `hl_account.py`'s finding 4: core coins arrive bare (`BTC`), HIP-3 dex coins arrive
 * prefixed (`xyz:AMZN`), and the server has ALREADY normalised positions, orders and
 * mids onto the prefixed form via `hl_check._prefix_coin`. The scan's alerts table
 * stores the same prefixed spelling (`xyz:SOFTBANK`, measured against the 67 live
 * rows). So the two sides join byte for byte and `joinKey` does nothing -- which is
 * the point of naming it.
 *
 * THE TRAP IT GUARDS. `marketCell` splits `xyz:NVDA` into `NVDA` plus an `xyz` chip
 * for DISPLAY. Joining on that displayed ticker instead of the stored one silently
 * loses every one of his 19 xyz positions -- and would additionally confuse a core
 * `NVDA` with `xyz:NVDA`, which are different markets on different clearinghouses.
 * `setupstate.test.ts` pins that with a case that fails on a bare-vs-prefixed compare.
 *
 * WHAT MAY BE ASSERTED, AND WHAT A FAILED DEX READ FORBIDS
 * -------------------------------------------------------
 * The house rule is that a chart element must never assert a price it is not drawn
 * at. The same rule applies to these badges, and it cuts asymmetrically:
 *
 *   * PRESENCE is safe. A position we read IS a position; an order we read IS an
 *     order. "On track", "Check this" and "Armed" are presence claims, and a dex we
 *     failed to read cannot un-hold a position we did read.
 *   * ABSENCE is not. "No setup justifies this order" is a claim about EVERY setup,
 *     and a setup living on a dex whose read failed is a setup we cannot see. So when
 *     `dex_errors` is non-empty the inverse join reports `unknown` -- never "No
 *     setup".
 *
 * That asymmetry is the whole safety argument of this file. Telling him to cancel a
 * protective order because a read failed is the one outcome here that loses money.
 *
 * AND ONLY AN ENTRY ORDER CAN ARM A SETUP
 * --------------------------------------
 * A reduce-only order is not a trade waiting to happen, it is the stop or target
 * PROTECTING a trade that already exists. Measured against his live book: of 100
 * resting orders, 90 are reduce-only protection and 10 are resting entries. Counting
 * protection as "armed" would badge a setup off its own stop loss, and -- far worse --
 * would list 90 stops and targets as "candidates for cancelation". Every claim in
 * this file about arming is therefore restricted to non-reduce-only, non-trigger
 * limit orders.
 *
 * NOTHING HERE CANCELS, PLACES OR MODIFIES AN ORDER, and there is no code path from
 * it that could. "Candidates for cancelation" means SHOW THEM.
 */
import type { OrderRow, PositionRow, Snapshot } from './tradepanel';

/** One setup row, as `/api/setups` serves it. Structural, so this module does not
 *  import the renderer's type and create a cycle. */
export type SetupLike = {
  asset: string;
  direction: string;
  entry: number | null;
  sl: number | null;
  sent_at: number | null;
};

/**
 * THE ONE JOIN KEY across setups, positions and orders: the coin EXACTLY as stored.
 *
 * `hl_check._prefix_coin` already re-attached the dex prefix server-side, so both
 * sides of this join speak the prefixed form and this function is identity. It exists
 * so there is ONE place the key is decided, and so the guard test has something to
 * point at: joining on the bare ticker `marketCell` displays loses all 19 xyz
 * positions and conflates `NVDA` with `xyz:NVDA`.
 *
 * No trim, no case fold. `xyz:AAPL` and `XYZ:AAPL` are different partitions of the
 * table the scan wrote and different symbols to the venue.
 */
export const joinKey = (coin: string): string => coin;

/** Is this setup's direction LONG? `bullish`/`bearish` is the scan's vocabulary and
 *  long/short is the book's; the two are mapped HERE and nowhere else, so a second
 *  opinion about which way a setup points cannot drift into existence. */
export const setupIsLong = (direction: string): boolean => direction === 'bullish';

/**
 * A resting order that could OPEN the trade a setup describes.
 *
 * Reduce-only is excluded because it closes rather than opens (see the module note),
 * and a trigger order is excluded because it is not resting AT a price: it is armed to
 * become a market order once price reaches one, so "a limit order in place at the
 * level" is not what it is.
 */
export const isRestingEntry = (o: OrderRow): boolean =>
  !o.reduce_only && !o.is_trigger && o.limit_px !== null && Number.isFinite(o.limit_px)
  && (o.limit_px as number) > 0;

/** A resting entry BUY opens a long, a resting entry SELL opens a short. Mapped in
 *  the same one place as `setupIsLong`, and deliberately from the order's SIDE rather
 *  than from where its price sits relative to spot: a buy below price and a long
 *  position are different facts, and the side is the one that says which way the fill
 *  would point. */
export const orderIsLong = (o: OrderRow): boolean => o.side === 'buy';

/**
 * How far a resting order may sit from the setup's entry and still be AT that level,
 * as a fraction of the setup's OWN risk distance.
 *
 * WHY A FRACTION OF THE RISK, NOT A FLAT PERCENTAGE. A flat 0.5% is a guess that
 * cannot be right across a watchlist spanning NATGAS at 2.69 and BTC at 100000+ --
 * and, worse, it is unrelated to the thing that actually decides whether the order is
 * the trade on the card. `|entry - sl|` IS that thing: it is the risk the scan sized
 * the trade at (`approach.stop_target` puts the stop just beyond the zone's far edge
 * since 2026-08-15 — structural, with `backtest.STOP_ATR` xATR only as the floor/fallback
 * — so the distance is the zone's own width rather than one fixed multiple), so it is
 * volatility and structure of the very market and
 * timeframe the row was measured on, carried in the payload already.
 *
 * 0.25 of it, because R is what a fill at the wrong price actually costs. An order a
 * quarter of the stop distance away from the advised entry turns a 1:2 card into
 * roughly a 1:1.6 or a 1:2.6 trade depending which side it drifted -- close enough to
 * still be that setup, and any wider and the R on the card stops describing the order.
 *
 * DEGRADES BY REFUSING TO CLAIM, NOT BY WIDENING. `sl` is null when the row carries no
 * archived stop -- for the 41 pre-2026-08-09 prod rows of 116, `levels_source:
 * 'not_recorded'`, meaning no stop was ever archived for that card. NOT a failed ATR
 * recovery: measured through `setups.py::atr_at_send` against prod, all 41 of those rows
 * have a RECOVERABLE ATR and none is refused for want of one -- the server deliberately
 * declines to re-derive a level with today's rule (card t_7d38d636), because the stop rule
 * changed under the archive. Either way there IS no risk distance and no tolerance can be
 * derived from one. Substituting a percentage would be inventing the measurement this whole
 * definition exists to avoid, so `levelTolerance` returns null and every caller
 * reports that it cannot tell.
 */
export const ARM_RISK_FRACTION = 0.25;

/** The tolerance in price units, or null when the row carries no risk distance. */
export function levelTolerance(s: SetupLike): number | null {
  if (s.entry === null || s.sl === null) return null;
  if (!Number.isFinite(s.entry) || !Number.isFinite(s.sl)) return null;
  const risk = Math.abs(s.entry - s.sl);
  return risk > 0 ? risk * ARM_RISK_FRACTION : null;
}

/** Is this order resting AT this setup's level, in the same direction?
 *
 *  `null` -- not false -- when the row has no risk distance to measure against. The
 *  caller must render that as "cannot tell" rather than as "not armed", because a
 *  missing measurement is not a negative result. */
export function armsSetup(s: SetupLike, o: OrderRow): boolean | null {
  if (joinKey(o.coin) !== joinKey(s.asset)) return false;
  if (!isRestingEntry(o)) return false;
  if (orderIsLong(o) !== setupIsLong(s.direction)) return false;
  const tol = levelTolerance(s);
  if (tol === null) return null;
  return Math.abs((o.limit_px as number) - (s.entry as number)) <= tol;
}

/** The state of one setup row against the book. `kind` drives the copy and the class;
 *  `text` is the whole cell and is always a sentence, never a tick that cannot be
 *  justified. */
export type SetupState = {
  kind: 'on_track' | 'check' | 'armed' | 'none' | 'unknown';
  text: string;
  /**
   * WHICH RESTING ENTRY ORDER ARMS THIS ROW -- the only thing on this screen that may
   * be cancelled, and the reason this field exists at all.
   *
   * `setupState` used to compute the `armed` verdict and throw the identity of the
   * order away, so the row could SAY an order was waiting but could not name it. A
   * Cancel button needs the identity, and it must be THE ORDER'S OWN oid: 83 of his
   * 92 open orders are stops and targets on 28 live positions, and cancelling one
   * silently removes protection from a real leveraged trade.
   *
   * *** NON-NULL ON `armed` AND ON NOTHING ELSE. *** It is populated from the order
   * `armsSetup` matched, which is behind `isRestingEntry` -- so a reduce-only or
   * trigger order can never reach this field, and therefore can never reach a button.
   * An `on_track` row is a POSITION, not an order: there is nothing to cancel there
   * and its protection must not be reachable from this table, so it carries no oid.
   *
   * *** AND NULL WHENEVER A DEX READ FAILED. *** Offering Cancel is offering to act on
   * an order we claim is still resting, and a book we could not fully read cannot
   * support that claim. `kind` and `text` are UNCHANGED in that case -- "Armed" is a
   * presence claim about an order we did read and stays true (see the module note on
   * the asymmetry) -- but the actionable half is withheld and the caller says it
   * cannot check.
   */
  oid?: number | null;
  /** The arming order's coin, as stored. The cancel path needs the market alongside
   *  the oid, and taking it from the ORDER rather than from the setup row means the
   *  two halves of the request can never disagree about which market they mean. */
  coin?: string | null;
};

/**
 * The badge for ONE setup row.
 *
 * ORDER OF PRECEDENCE IS DELIBERATE. `check` outranks everything: a live position
 * pointing the OPPOSITE way to what the scan just found is the one row on this screen
 * that needs a human, so it must not be hidden behind an "Armed" badge that happens
 * to also be true. Then `on_track` (the position agrees), then `armed` (no position
 * yet, but the order is waiting). A row with none of the three gets no badge and
 * asserts nothing, which is correct: most setups are simply not in his book.
 *
 * A position on the asset is matched WITHOUT regard to size or entry -- "an active
 * trade exists for the asset" is the question, and any non-zero position answers it.
 */
export function setupState(s: SetupLike, snap: Snapshot | null): SetupState {
  if (!snap) return { kind: 'unknown', text: 'account not loaded' };
  const key = joinKey(s.asset);
  const pos = snap.positions.find((p: PositionRow) => joinKey(p.coin) === key);
  if (pos) {
    const posLong = pos.side === 'long';
    // OPPOSITE FIRST. The scan says one thing and his book says the other; every
    // other state is a smaller fact than that one.
    if (posLong !== setupIsLong(s.direction)) {
      return { kind: 'check', text: `Check this: you are ${pos.side}` };
    }
    return { kind: 'on_track', text: `On track: already ${pos.side}` };
  }
  // A DEX WE COULD NOT READ CANNOT SUPPORT AN OFFER TO ACT. The badge below is a
  // presence claim and stays exactly as it was, but the oid is what a caller turns
  // into a Cancel button, and that button asserts the order is still there to cancel.
  const readFailed = Object.keys(snap.dex_errors ?? {}).length > 0;
  let unmeasurable = false;
  for (const o of snap.orders) {
    const armed = armsSetup(s, o);
    if (armed === null) unmeasurable = true;
    else if (armed) {
      // The oid comes off THE ORDER `armsSetup` matched -- never from the row's
      // position in a list, which sorting reorders under the reader.
      return {
        kind: 'armed',
        text: 'Armed at this level',
        oid: readFailed ? null : o.oid,
        coin: readFailed ? null : o.coin,
      };
    }
  }
  // A resting entry on the right side of the right market, and no way to measure
  // whether it sits at the level. Saying "not armed" would be a claim; saying nothing
  // would hide an order he has money in.
  if (unmeasurable) return { kind: 'unknown', text: 'Order resting, no level to check' };
  return { kind: 'none', text: '' };
}

/**
 * Is this row the LATEST setup for its asset?
 *
 * The scan sends several alerts per asset over days -- 15 of the 48 assets in the
 * live table have more than one row, up to 3. Andrei wants "the latest and greatest".
 *
 * OLDER ROWS ARE DE-EMPHASISED, NOT HIDDEN. Hiding them would delete the only place
 * the scan's history is visible in the app, and this table IS that record: an empty
 * space where a superseded setup was reads as "the scan never looked", which is the
 * exact class of silent absence this repo has already been bitten by. `renderAdvice`
 * made the same call for its no-change rows and renders them `quiet`, so this reuses
 * that treatment rather than inventing a second one.
 *
 * Ties on `sent_at` keep the payload's order (it arrives newest-first), so the first
 * row seen for an asset wins. A row with no `sent_at` cannot claim to be the latest,
 * so it never displaces one that can.
 */
export function latestKeys(setups: SetupLike[]): Set<number> {
  const best = new Map<string, number>();
  setups.forEach((s, i) => {
    const key = joinKey(s.asset);
    const seen = best.get(key);
    if (seen === undefined) {
      best.set(key, i);
      return;
    }
    const mine = s.sent_at ?? -Infinity;
    const theirs = setups[seen].sent_at ?? -Infinity;
    if (mine > theirs) best.set(key, i);
  });
  return new Set(best.values());
}

/** WHERE ANDREI LEFT OFF, as a row identity plus the position it was at.
 *
 *  The key is what makes it survive a re-fetch: the list is re-read on every visit to
 *  the tab, and an INDEX alone would point at a different setup the moment the scan
 *  writes a new row (every new alert shifts the whole newest-first list down by one).
 *  The index is kept only as the fallback for a row the payload no longer carries. */
export type SetupCursor = { key: string; index: number };

/** A stable identity for one setup row, and it is now the row's REAL primary key.
 *
 *  `asset|tf|direction|sent_at|entry` was NOT unique: it folded prod's 87 rows onto 83
 *  identities, because four adjacent pairs are byte-identical on every field the payload
 *  carried. `resolveCursor` returns the FIRST match, so a cursor stored for the second row
 *  of a pair resolved to the first, `tpStepSetup` re-resolved and stepped straight back to
 *  the second, and the walk was an ABSORBING STATE: 22 of 87 rows visited going forward,
 *  65 unreachable, and `prev` skipped the second of each pair (t_1849cfb1).
 *
 *  `idm_swept_time` is what fixes it AT THE SOURCE rather than papering over it downstream:
 *  it is inside the DynamoDB sort key (`alert_log.py::sort_key(tf, idm_swept_time, zone_lo,
 *  zone_hi)`), so it is the field that makes two alerts two rows, and `/api/setups` now
 *  sends it. MEASURED: all 87 prod rows distinct, 4 collisions to 0.
 *
 *  *** IT ENTERS THE IDENTITY AND NOTHING ELSE. *** The order is `sent_at`'s
 *  (`docs/decisions/setups-review-flow.md` §3); the two fields disagree on 87 of 87 rows
 *  with a gap to 197.5 days, so a sort on the sweep would hoist a June sweep above
 *  yesterday's. Both suites pin that the ORDER never keys off it.
 *
 *  Byte for byte on `asset`, like every other key in this file: `xyz:AAPL` and
 *  `XYZ:AAPL` are different rows. */
export function setupKey(s: SetupKeyable): string {
  return [joinKey(s.asset), s.tf, s.direction, s.sent_at ?? '', s.entry ?? '',
    s.idm_swept_time ?? ''].join('|');
}

export type SetupKeyable = {
  asset: string; tf: string; direction: string;
  sent_at: number | null; entry: number | null; idm_swept_time: number | null;
};

/** Which row the cursor points at NOW, or null when there is nothing to point at.
 *
 *  A STALE CURSOR IS NOT RESURRECTED. When the remembered row is gone from the payload
 *  (the scan rewrote the table, or a `limit` shortened it) the cursor falls back to the
 *  same POSITION, clamped into range -- the nearest surviving row, which is the one that
 *  took its place in the same newest-first list. Pointing at a row that no longer exists
 *  would highlight, and next/prev from, a setup nobody opened.
 *
 *  AMBIGUITY IS BROKEN BY THE STORED INDEX, WHICH IS THE STALL GUARD (t_1849cfb1). The
 *  identity above is unique across all 87 prod rows, so this should never fire -- but
 *  `findIndex` returning the FIRST of a duplicate pair is what made the walk an absorbing
 *  state, and it fired because a field went missing from the payload, which can happen
 *  again. So when the key matches MORE THAN ONE row, the match nearest the cursor's own
 *  stored index wins: the walk then still terminates and still reaches every row, because
 *  `next` writing index 22 resolves back to 22 rather than to 21. Belt and braces to the
 *  identity, not a substitute for it -- the exact-identity path is what lets the cursor
 *  survive the scan writing new rows above it, which is the feature. */
export function resolveCursor(setups: SetupKeyable[], cursor: SetupCursor | null): number | null {
  if (!cursor || !setups.length) return null;
  const matches: number[] = [];
  setups.forEach((s, i) => { if (setupKey(s) === cursor.key) matches.push(i); });
  if (matches.length === 1) return matches[0];
  if (matches.length > 1) {
    return matches.reduce((best, i) =>
      Math.abs(i - cursor.index) < Math.abs(best - cursor.index) ? i : best);
  }
  return Math.max(0, Math.min(setups.length - 1, cursor.index));
}

/** WHERE THE CURSOR IS IN THE WALK: `at` 1-based, `of` the list length, and whether each
 *  edge has anywhere to go.
 *
 *  PURE AND HERE rather than inline in the controller, because a test that re-declares this
 *  arithmetic proves only that the test can add one. `main.ts::tkReviewNav` decides WHETHER
 *  to show the walk (the ticket must still hold that setup's market); this decides what it
 *  says once shown.
 *
 *  EDGES DISABLE, THEY NEVER WRAP. Wrapping from the last setup back to the first silently
 *  restarts a review that looked finished. */
export function reviewNavAt(count: number, at: number) {
  return { at: at + 1, of: count, hasPrev: at > 0, hasNext: at < count - 1 };
}

/** One step up (`-1`) or down (`+1`) the DISPLAYED list, or null at the edge.
 *
 *  Null rather than a clamp: a clamped step LOOKS like it moved and did not, which is the
 *  same "the app thinks I am somewhere I am not" defect the cursor exists to remove. The
 *  buttons are disabled at the edges and this is the second guard for a keyboard or a stale
 *  render. */
export function stepIndex(count: number, at: number, delta: -1 | 1): number | null {
  const next = at + delta;
  return next < 0 || next >= count ? null : next;
}

/** A resting entry order that no setup accounts for -- his "candidates for
 *  cancelation" -- or one we cannot judge. */
export type LooseOrder = {
  order: OrderRow;
  kind: 'none' | 'unknown';
  text: string;
};

/**
 * THE INVERSE JOIN: iterate his ORDERS and ask which no setup justifies.
 *
 * Only resting ENTRY orders are considered. His stops and targets are reduce-only and
 * are the protection on live trades; listing them as cancellation candidates would be
 * the most dangerous thing this feature could print (90 of his 100 resting orders are
 * exactly that).
 *
 * *** A FAILED DEX READ DOWNGRADES EVERY VERDICT HERE TO `unknown`. *** This is an
 * ABSENCE claim over the whole setup list, and `/api/setups` scans one table while
 * the book is swept per dex: if a dex read failed, a market we hold an order on may
 * have a setup we never saw. `dex_errors` non-empty therefore means the answer is "we
 * could not check", and the words say so. Presence badges elsewhere in this file are
 * unaffected -- a position we read is still a position.
 *
 * `sl`-less setups are also honoured as a reason NOT to accuse an order: if a setup
 * exists on the same market in the same direction but carries no risk distance, we
 * cannot tell whether the order is at its level, so the order is `unknown` rather
 * than orphaned. An unjudgeable order must not be reported as an unjustified one.
 */
export function looseOrders(setups: SetupLike[], snap: Snapshot | null): LooseOrder[] {
  if (!snap) return [];
  const readFailed = Object.keys(snap.dex_errors ?? {}).length > 0;
  const out: LooseOrder[] = [];
  for (const o of snap.orders) {
    if (!isRestingEntry(o)) continue;
    let unmeasurable = false;
    let justified = false;
    for (const s of setups) {
      const armed = armsSetup(s, o);
      if (armed === null) unmeasurable = true;
      else if (armed) {
        justified = true;
        break;
      }
    }
    if (justified) continue;
    if (readFailed) {
      out.push({
        order: o,
        kind: 'unknown',
        // NOT "orphaned". The setup that justifies this order may live on the dex we
        // could not read.
        text: 'Cannot check: a market read failed',
      });
      continue;
    }
    out.push(unmeasurable
      ? { order: o, kind: 'unknown', text: 'Cannot check: setup has no level' }
      : { order: o, kind: 'none', text: 'No setup' });
  }
  return out;
}
